import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import StatusBadge from "@/components/common/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Airplane, CheckCircle, XCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth, can } from "@/contexts/AuthContext";

const CHECKLIST = [
  ["account_active", "Account is active"],
  ["no_duplicate", "No duplicate account"],
  ["no_suspicious_trading", "No suspicious trading"],
  ["no_fake_volume", "No fake volume"],
  ["no_internal_transfer_abuse", "No internal transfer abuse"],
  ["broker_rules_followed", "Broker rules followed"],
  ["valid_lot_confirmed", "Valid closed lot confirmed"],
  ["not_disqualified", "Participant is not disqualified"],
  ["active_during_campaign", "Participant remained active during campaign"],
];

export default function BaliQualifiers() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [checks, setChecks] = useState({});
  const [notes, setNotes] = useState("");

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/bali-qualifiers"); setRows(data); } catch {}
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openAudit = (p) => {
    setSelected(p);
    setChecks(CHECKLIST.reduce((a, [k]) => ({ ...a, [k]: false }), {}));
    setNotes("");
  };

  const submit = async (decision) => {
    if (!selected) return;
    try {
      await api.post(`/bali-qualifiers/${selected.id}/audit`, {
        ...checks,
        notes,
        decision,
      });
      toast.success(`Audit ${decision}`);
      setSelected(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-6 md:p-8 max-w-[1600px] mx-auto">
      <PageHeader title="Bali Qualifiers" subtitle="Participants who reached 200 lot must pass the audit to qualify for the Bali trip." breadcrumbs="Bali" testId="bali-page" />

      <div className="bg-[#111827] border border-white/10 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 bg-[#1F2937]/50 text-left text-[10px] uppercase tracking-widest text-gray-500">
                <th className="px-4 py-3">Participant</th>
                <th className="px-4 py-3">Account</th>
                <th className="px-4 py-3 text-right">Valid Lot</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={5} className="p-8 text-center text-gray-500">Loading…</td></tr> :
                rows.length === 0 ? <tr><td colSpan={5} className="p-8 text-center text-gray-500">No Bali candidates yet</td></tr> :
                rows.map((p) => (
                  <tr key={p.id} data-testid={`bali-row-${p.id}`} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="px-4 py-3 text-white">{p.full_name}</td>
                    <td className="px-4 py-3 font-mono text-gray-400">{p.broker_account_number}</td>
                    <td className="px-4 py-3 text-right font-mono text-orange-300 font-bold">{(p.total_valid_lot || 0).toFixed(2)}</td>
                    <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
                    <td className="px-4 py-3 text-right">
                      {can(user, "manage") && p.status !== "bali_qualified" && (
                        <button data-testid={`bali-audit-${p.id}`} onClick={() => openAudit(p)} className="px-3 py-1.5 text-xs bg-orange-500 hover:bg-orange-600 text-white rounded-md inline-flex items-center gap-1">
                          <Airplane size={12} /> Run Audit
                        </button>
                      )}
                      {p.status === "bali_qualified" && (
                        <span className="inline-flex items-center gap-1 text-xs text-orange-300"><CheckCircle size={14} weight="fill" /> Qualified</span>
                      )}
                    </td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="bg-[#111827] border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Airplane size={20} className="text-orange-400" /> Bali Audit Checklist</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-400">{selected?.full_name} · <span className="font-mono">{selected?.broker_account_number}</span></p>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {CHECKLIST.map(([k, label]) => (
              <label key={k} className="flex items-center gap-3 p-2 rounded-md hover:bg-white/5 cursor-pointer">
                <input data-testid={`bali-check-${k}`} type="checkbox" checked={!!checks[k]} onChange={(e) => setChecks({ ...checks, [k]: e.target.checked })} />
                <span className="text-sm text-gray-300">{label}</span>
              </label>
            ))}
          </div>
          <textarea
            data-testid="bali-audit-notes"
            placeholder="Audit notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="w-full bg-[#0B0F19] border border-white/10 rounded-md p-3 text-sm text-white outline-none"
          />
          <DialogFooter>
            <button data-testid="bali-reject" onClick={() => submit("rejected")} className="px-4 py-2 text-sm bg-red-500/10 border border-red-500/30 text-red-300 rounded-md hover:bg-red-500/20">
              <XCircle size={14} className="inline mr-1" /> Reject
            </button>
            <button data-testid="bali-approve" onClick={() => submit("approved")} className="px-4 py-2 text-sm bg-orange-500 hover:bg-orange-600 text-white rounded-md">
              <CheckCircle size={14} className="inline mr-1" /> Approve Bali
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
